// The team directory: who your colleagues are, and one click to ring them.
//
// Internal calling already worked at the switch — each seat is an extension in
// its organization's dialplan context — but there was nowhere in the portal to
// see who those extensions belong to, so calling a colleague meant knowing their
// number by heart.
//
// Reads /v1/telephony/directory, NOT the extensions admin API. That one is
// admin-only and returns the seat entity including its SIP password; a colleague
// list must never be built from it.
import React, { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Search, Users, RefreshCw, ShieldAlert, Forward } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { directoryService, DirectoryEntry } from 'api/services/directory.service';
import { clickToCall } from 'api/services/agentConsole.service';
import { subscribe, getState, actions } from 'telephony/softphoneStore';

const Directory: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const role = (currentOrganization?.role || user?.role || '').toLowerCase();

  const phone = useSyncExternalStore(subscribe, getState);
  const [entries, setEntries] = useState<DirectoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  // What click-to-call is doing, so a button press is never silent.
  const [ringing, setRinging] = useState<string | null>(null);
  const [callError, setCallError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setEntries(await directoryService.list());
    } catch (e: any) {
      setError(e?.response?.status === 403
        ? 'Your account cannot read the team directory.'
        : 'Could not load the directory.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter(e =>
      e.extension.toLowerCase().includes(needle) || e.displayName.toLowerCase().includes(needle));
  }, [entries, q]);

  // A dial button is only honest when there is a phone to dial from. The
  // softphone is deliberately limited to the highest-trust roles, so for
  // everyone else this page is a directory to read, not to call from.
  const canDial = phone.phase === 'registered';
  const busy = phase_is_busy(phone.phase);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Users className="w-5 h-5" /> Team directory
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Colleagues in your organization. Dialling one rings their extension directly.
          </p>
        </div>
        <button
          onClick={() => void load()}
          className="inline-flex items-center gap-1.5 rounded border border-gray-300 px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {!canDial && (
        // NOT "you cannot call": without a browser phone the switch rings your
        // own number instead, which is a smaller restriction than it sounds and
        // worth saying plainly rather than leaving a disabled button.
        <div className="mb-4 rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
          <ShieldAlert className="mb-0.5 mr-1.5 inline h-4 w-4" />
          Your softphone is not connected, so Call will ring <strong>your own phone</strong> first
          and then connect you. Open the{' '}
          <Link to="/telephony/softphone" className="underline">softphone</Link> to call from the browser instead.
        </div>
      )}
      {ringing && (
        <div className="mb-4 rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-800 dark:border-green-700 dark:bg-green-900/20 dark:text-green-300">
          Calling <span className="font-mono">{ringing}</span> — answer your phone and you will be connected.
        </div>
      )}
      {callError && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
          {callError}
        </div>
      )}

      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search name or extension…"
          className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        />
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
        {loading ? (
          <p className="p-6 text-center text-sm text-gray-500">Loading…</p>
        ) : error ? (
          <p className="p-6 text-center text-sm text-red-600 dark:text-red-400">{error}</p>
        ) : shown.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-500">
            {entries.length === 0
              ? 'No colleagues yet. Seats appear here once they are created and enabled.'
              : 'Nobody matches that search.'}
          </p>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {shown.map(e => (
              <li key={e.extension} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 items-start gap-2">
                  <span
                    title={presenceLabel(e.presence)}
                    className={`mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${presenceDot(e.presence)}`}
                  />
                  <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {e.displayName}
                    {e.followMeEnabled && (
                      <span
                        title="Calls follow this person to another number if their seat does not answer"
                        className="ml-2 inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      >
                        <Forward className="h-3 w-3" /> follow-me
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-xs text-gray-500">
                    {e.extension}
                    {e.self && <span className="ml-1.5 not-italic text-gray-400">— you</span>}
                  </div>
                  </div>
                </div>
                <button
                  disabled={busy || e.self}
                  onClick={async () => {
                    setCallError(null);
                    if (canDial) return actions.call(e.extension);
                    // No browser phone: the switch rings this user first.
                    try {
                      const r = await clickToCall(e.extension);
                      setRinging(r?.ringing || 'your phone');
                      window.setTimeout(() => setRinging(null), 15000);
                    } catch (err: any) {
                      setCallError(err?.response?.data?.message
                        || 'That call could not be placed. Your profile may have no phone number.');
                    }
                  }}
                  title={e.self ? 'This is your own seat'
                    : busy ? 'Already on a call'
                    : canDial ? `Call ${e.displayName}` : `Ring my phone, then connect to ${e.displayName}`}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Phone className="h-4 w-4" /> Call
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/**
 * Colour for a colleague's device state.
 *
 * Unknown is GREY, never green. A directory that guesses "available" for a
 * colleague whose state it could not read invites a call that rings out, which
 * is worse than admitting the status is unknown.
 */
function presenceDot(p: string | null) {
  if (!p) return 'bg-gray-300 dark:bg-gray-600';
  const s = p.toLowerCase();
  if (s.includes('unavailable')) return 'bg-gray-400';         // not registered
  if (s.includes('in use') && !s.includes('not in use')) return 'bg-amber-500';
  if (s.includes('ringing')) return 'bg-purple-500';
  if (s.includes('not in use') || s.includes('idle')) return 'bg-green-500';
  return 'bg-gray-300 dark:bg-gray-600';
}

function presenceLabel(p: string | null) {
  if (!p) return 'Status unknown';
  const s = p.toLowerCase();
  if (s.includes('unavailable')) return 'Offline — their phone is not registered';
  if (s.includes('not in use') || s.includes('idle')) return 'Available';
  if (s.includes('in use')) return 'On a call';
  if (s.includes('ringing')) return 'Ringing';
  return p;
}

/** On a call already — dialling a second one from here would drop the first. */
function phase_is_busy(phase: string) {
  return phase === 'ringing' || phase === 'calling' || phase === 'in-call';
}

export default Directory;
